/** Card 1 — demo FAST-style journey assessment. Anonymous, nothing stored. */
import React, { useState } from "react";
import { ROADMAP_STAGES } from "@/lib/roadmapStages";
import { TransitionPanel, DemoBadge, OptionButton, trackStep, trackCompleted, saveJourneyStage } from "../activationShared";

const QUESTIONS = [
    { q: "Are they repeating questions or forgetting recent conversations?", opts: ["Rarely or never", "Sometimes", "Often"] },
    { q: "How are complex tasks going — bills, planning meals, managing medications?", opts: ["Handles them fine", "Some slips lately", "Needs real help now"] },
    { q: "Do they need help choosing clothing appropriate for the day or weather?", opts: ["No", "Occasionally", "Yes, regularly"] },
    { q: "Do they need help with dressing, bathing, or using the bathroom?", opts: ["No", "Occasionally", "Yes, regularly"] },
    { q: "Have there been episodes of incontinence?", opts: ["No", "Occasionally", "Frequently"] },
    { q: "Is speech limited to just a few words, or is walking becoming difficult?", opts: ["No", "Somewhat", "Yes"] },
];

// FAST is hierarchical — the furthest question with a strong answer sets the estimate.
const STAGE_BY_Q = ["first_concerns", "getting_answers", "diagnosis_next", "daily_care", "transitions", "advanced_care"];

function estimate(answers) {
    let key = "first_concerns";
    answers.forEach((a, i) => {
        if (a === 2) key = STAGE_BY_Q[Math.min(i + 1, STAGE_BY_Q.length - 1)];
        else if (a === 1 && key === "first_concerns") key = STAGE_BY_Q[i];
    });
    return ROADMAP_STAGES.find((s) => s.key === key) || ROADMAP_STAGES[0];
}

export default function SampleJourney({ onClose }) {
    const [answers, setAnswers] = useState([]);
    const idx = answers.length;
    const done = idx >= QUESTIONS.length;
    const stage = done ? estimate(answers) : null;

    const pick = (v) => {
        const next = [...answers, v];
        setAnswers(next);
        trackStep("sample_journey", `q${idx + 1}`);
        if (next.length >= QUESTIONS.length) {
            trackCompleted("sample_journey");
            saveJourneyStage(estimate(next).key);
        }
    };

    return (
        <div data-testid="activation-sample-journey">
            <DemoBadge>Sample assessment — anonymous, nothing is saved</DemoBadge>
            {!done ? (
                <>
                    <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-[#6E7A74]">Question {idx + 1} of {QUESTIONS.length}</p>
                    <h3 className="mt-1 font-heading font-medium text-lg md:text-xl text-[#1F2624]">{QUESTIONS[idx].q}</h3>
                    <div className="mt-4 space-y-2">
                        {QUESTIONS[idx].opts.map((o, v) => (
                            <OptionButton key={o} onClick={() => pick(v)} testid={`journey-q${idx + 1}-opt${v}`}>{o}</OptionButton>
                        ))}
                    </div>
                    <p className="mt-4 text-xs text-[#9CA6A1]">Not sure? Answer for a fictional loved one just to see how it works.</p>
                </>
            ) : (
                <div data-testid="journey-result">
                    <p className="mt-4 text-xs font-semibold uppercase tracking-wider" style={{ color: stage.color }}>Estimated place on the journey</p>
                    <h3 className="mt-1 font-heading font-semibold text-2xl text-[#12776F]">{stage.title}</h3>
                    {stage.fast_label && <p className="text-sm text-[#6E7A74] mt-0.5">{stage.fast_label}</p>}
                    <p className="mt-3 text-sm text-[#2A3330] leading-relaxed">{stage.meaning}</p>
                    <p className="mt-4 text-sm font-semibold text-[#1F2624]">What families usually do next:</p>
                    <ul className="mt-2 space-y-1.5">
                        {(stage.next_steps || []).slice(0, 4).map((s) => (
                            <li key={s} className="text-sm text-[#4C5551] leading-relaxed pl-4 relative">
                                <span className="absolute left-0 top-[7px] w-1.5 h-1.5 rounded-full" style={{ backgroundColor: stage.color }} />{s}
                            </li>
                        ))}
                    </ul>
                    <p className="mt-4 text-xs text-[#9CA6A1]">Educational only — not a diagnosis. Only a clinician can assess your loved one.</p>
                    <TransitionPanel card="sample_journey" onClose={onClose} ctaLabel="Save your results with a free account" />
                </div>
            )}
        </div>
    );
}
